import type { SubjectOf } from './subjects'
import { type Failure, RequestFailed, malformed, readJson, request } from './request'

/**
 * The notepad, as the service describes it.
 *
 * The whole pad arrives in one request, texts and all, and that is deliberate: the pad shows each note's
 * first line *drawn as it was written*, so the browser needs the markup anyway, and tapping a note opens
 * it for editing straight away. A pad is a handful of short texts — that is what the four-day retention
 * is for — so one request brings all of it and opening a note costs nothing.
 *
 * **Saving goes through one function**, {@link saveNote}, and so does the library's. That is not tidiness:
 * the issue about writing without a connection puts a buffer in front of exactly these two, and one place
 * per area is what makes that an addition rather than a rewrite.
 */

export interface Note {
  readonly id: string
  /** What was written, as it was written. Empty is ordinary — a note is made before anything is typed. */
  readonly text: string
  /** Which markup it is written in, as a media type: `text/markdown` or `text/asciidoc`. */
  readonly format: string
  /** When it was struck through, or `null` while it is open. A struck note is drawn through and last. */
  readonly struckAt: string | null
  readonly createdAt: string
  readonly updatedAt: string
  /** The subjects it belongs to. Always filed by hand — there is no folder to inherit from. */
  readonly subjects?: readonly SubjectOf[]
  /** How far the search has got with it. `without-text` for a note nobody has typed into yet. */
  readonly textState: string
}

const NOTES = '/api/notes'

export async function fetchNotes(signal?: AbortSignal): Promise<Note[]> {
  const response = await request(NOTES, { signal })
  const payload: unknown = await response.json().catch(() => null)
  if (!Array.isArray(payload)) {
    throw malformed('The answer did not describe a notepad.')
  }
  return payload as Note[]
}

/**
 * A new note at the top, with what was written in it.
 *
 * **The text goes with the request that makes it.** A note that says nothing is not a note and the
 * service refuses one, so there is no moment at which a blank one exists — not even briefly, and not
 * waiting for something to sweep it up. Until the first keystroke there is a draft on the screen and
 * nothing at all on the other side.
 */
export async function writeNote(text: string, format?: string): Promise<Note> {
  return (await change(NOTES, 'POST', { text, format })) as Note
}

/**
 * The same note, written in another markup from now on.
 *
 * **Nothing but the language is sent**, because nothing but the language changes. Its own address
 * rather than a field on {@link saveNote}: that one is called while somebody types, and a language
 * that could change on a keystroke is not a language anybody chose.
 */
export async function changeNoteMarkup(id: string, format: string): Promise<Note> {
  return (await change(`${NOTES}/${encodeURIComponent(id)}/markup`, 'PUT', { format })) as Note
}

/**
 * What has been typed, saved.
 *
 * Called while somebody types, so it is the ordinary case rather than an event. A note keeps no earlier
 * states — it is a scribble, and it has no history by design.
 */
export async function saveNote(id: string, text: string): Promise<Note> {
  return (await change(`${NOTES}/${encodeURIComponent(id)}/text`, 'PUT', { text })) as Note
}

/**
 * Puts a note at `index` among the open ones, counted from the top and from 0.
 *
 * An index rather than a neighbour, because that is what both ways of asking produce: dragging a note
 * onto a place, and the two visible controls beside it. The second is not a fallback — nothing in MyLib
 * may be reachable by gesture alone.
 */
export async function placeNote(id: string, index: number): Promise<Note> {
  return (await change(`${NOTES}/${encodeURIComponent(id)}/place`, 'PUT', { index })) as Note
}

/** Strikes a note through. It goes to the end and can be brought back for four days. */
export async function strikeNote(id: string): Promise<Note> {
  return (await change(`${NOTES}/${encodeURIComponent(id)}`, 'DELETE')) as Note
}

export async function bringNoteBack(id: string): Promise<Note> {
  return (await change(`${NOTES}/${encodeURIComponent(id)}/restore`, 'POST')) as Note
}

/**
 * Removes a struck note now, rather than waiting out the four days.
 *
 * The second stage of striking one — the same shape the library's trash has, where the first stage is
 * undone by a control standing right beside it and this one is the deliberate act that removes.
 */
export async function removeNoteForGood(id: string): Promise<void> {
  await change(`${NOTES}/${encodeURIComponent(id)}/forever`, 'DELETE')
}

/** The first line of a note, which is what the pad shows of it. Empty for one nobody has typed into. */
export function firstLineOf(note: Note): string {
  return (
    note.text
      .split('\n')
      .map((line) => line.trim())
      .find((line) => line !== '') ?? ''
  )
}

/** The statuses that carry a code this side knows how to say out loud. */
const REFUSALS = [400, 404, 409, 413] as const

const SENTENCES: Readonly<Record<string, string>> = {
  'no-such-note': 'That note is not there any more.',
  'note-says-nothing': 'A note has to say something.',
  'note-too-long':
    'That is longer than a note holds. Something this long wants to be a document — make one in the library and write it there.',
}

async function change(address: string, method: string, body?: unknown): Promise<unknown> {
  const response = await request(address, {
    method,
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    expect: [...REFUSALS],
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  })

  if (!response.ok) {
    throw refusalIn(await response.text(), response.status)
  }

  return response.status === 204 ? null : readJson<unknown>(response)
}

function parse(body: string): { code?: string; message?: string } | null {
  try {
    return JSON.parse(body) as { code?: string; message?: string }
  } catch {
    return null
  }
}

function refusalIn(body: string, status: number): RequestFailed {
  const problem = parse(body)
  const known = problem?.code === undefined ? undefined : SENTENCES[problem.code]

  const failure: Failure = {
    kind: status >= 500 ? 'service' : 'refused',
    message: known ?? problem?.message ?? 'MyLib could not do that.',
    detail: known === undefined ? null : (problem?.message ?? null),
    // Nothing on this list gets better by being asked again: each of them is an answer.
    retryable: status >= 500,
    code: problem?.code ?? null,
  }
  return new RequestFailed(failure)
}
